import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import PaymentButton from '@acorn-portal/shared/src/components/buttons/PaymentButton';
import PriceBreakdown from '@acorn-portal/shared/src/components/display/price-breakdown';
import DisplayField from '@acorn-portal/shared/src/components/displayField';
import RadioCard from '@acorn-portal/shared/src/components/radioCard';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { handTakeUpMidTermAdjustment } from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import StripePaymentWrapper from '@acorn-portal/shared/src/pages/mta/components/StripePaymentWrapper';
import BraintreeCardPaymentSection from '@acorn-portal/shared/src/pages/mta/components/braintree-card-payment-section';
import { MtaPaymentCollectionOption, PaymentMethods, PaymentProvider2 } from '@acorn-portal/shared/src/services/api';
import { formatPounds, isProtect, isProtectYD } from '@acorn-portal/shared/src/utils';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Box, Collapse, Grid, RadioGroup, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';

interface IProps {
	toggleModal: (open: boolean) => void;
	setLoading: (loading: boolean) => void;
	financeOption: MtaPaymentCollectionOption;
	payInFullOption: MtaPaymentCollectionOption;
}

const CardAndFinancePaymentOptions = ({ toggleModal, setLoading, financeOption, payInFullOption }: IProps) => {
	const [selectedMethod, selectMethod] = useState<string>();
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const policy = useAppSelector((redux) => redux.fullPolicy);
	const contactPreferences = useAppSelector((redux) => redux.contactPreferences);
	const midTermAdjustmentResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);

	const isNonFinancePolicy = policy.nonFinancePolicy;

	const navigate = useNavigate();

	const handleInstallmentSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
		selectMethod(e.target.value);
	};

	const submitPaymentForm = async () => {
		setLoading(true);
		try {
			await handTakeUpMidTermAdjustment({
				policyReference: midTermAdjustment.policyReference,
				sendPostalDocuments: contactPreferences.printToPost,
				transactionId: midTermAdjustment.transactionId,
				takeUpDetails: {
					underwritingResponseId: midTermAdjustmentResponse.underwritingOutcomeResponseId,
					underwritingOptionId: midTermAdjustmentResponse.optionId,
					paymentCollectionOptionCode: financeOption.code,
					selectedBalanceSettlementMethod: PaymentMethods.DIRECT_DEBIT,
				},
			});
			setLoading(false);
			navigate(ROUTE_PATHS.makeAChangePaymentSuccess);
		} catch (error) {
			console.error('Error processing payment:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	const isProtectYoungDriver: boolean = isProtectYD(policy.product, policy.scheme ?? '');
	const isProtectAdultDriver: boolean = isProtect(policy.product, policy.scheme ?? '');

	const paymentPlanWording = () => {
		if (isProtectYoungDriver) {
			return 'Add to Direct Debit';
		}
		if (isProtectAdultDriver || isNonFinancePolicy) {
			return 'Add to Finance Plan';
		}
		return 'Add to Monthly Payment Plan';
	};

	return (
		<Box>
			<Grid container spacing={2} sx={{ mt: 1 }}>
				<Grid size={{ xs: 12, md: 12 }}>
					<RadioGroup
						onChange={handleInstallmentSelection}
						aria-labelledby='demo-radio-buttons-group-label'
						defaultValue='instalment'
						name='radio-buttons-group'
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: { xs: 'column', md: 'row' },
								gap: { xs: 1, md: 4 },
								paddingX: { md: 0 },
							}}
						>
							<RadioCard value='instalment' title={paymentPlanWording()} selected={selectedMethod === 'instalment'}>
								<Typography sx={{ fontSize: 14, mt: 2, lineHeight: 1 }}>Total Amount</Typography>
								<Typography
									sx={{
										lineHeight: 1,
										fontWeight: 'bold',
										fontSize: 35,
										color: 'primary.main',
									}}
								>
									{formatPounds(financeOption.totalAmountPayable ?? 0)}
								</Typography>

								<Grid container marginTop={2} gap={2}>
									{financeOption.unequalInstalmentAmount && financeOption.unequalInstalmentAmount > 0 ? (
										<>
											<Grid sx={{ minWidth: '22%' }}>
												<Typography
													sx={{
														fontSize: 12,
														height: { xs: 'auto', sm: 'auto' },
													}}
												>
													Next instalment
												</Typography>
												<Typography sx={{ fontSize: 14 }}>
													{formatPounds(financeOption.unequalInstalmentAmount ?? 0)}
												</Typography>
											</Grid>
											<Grid sx={{ minWidth: '22%' }}>
												<Typography
													sx={{
														fontSize: 12,
														height: { xs: 'auto', sm: 'auto' },
													}}
												>
													{financeOption.appliedInstalmentCount &&
														financeOption.appliedInstalmentCount > 1 &&
														financeOption.appliedInstalmentCount - 1}{' '}
													Remaining instalments
												</Typography>
												<Typography sx={{ fontSize: 14 }}>
													{formatPounds(financeOption.standardInstalmentAmount ?? 0)}
												</Typography>
											</Grid>
										</>
									) : (
										<Grid sx={{ minWidth: '22%' }}>
											<Typography
												sx={{
													fontSize: 12,
													height: { xs: 'auto', sm: 'auto' },
												}}
											>
												{financeOption.appliedInstalmentCount &&
													financeOption.appliedInstalmentCount > 1 &&
													financeOption.appliedInstalmentCount}{' '}
												Remaining instalments
											</Typography>
											<Typography sx={{ fontSize: 14 }}>
												{formatPounds(financeOption.standardInstalmentAmount ?? 0)}
											</Typography>
										</Grid>
									)}
								</Grid>

								<PriceBreakdown
									price={financeOption.totalAmountPayable}
									fee={midTermAdjustmentResponse.coverDetails.fee}
									product={policy.product}
								/>

								{!isProtectYoungDriver && (
									<Typography sx={{ mt: 2 }} fontSize={14}>
										{isNonFinancePolicy
											? 'Please be aware an interest rate applies. For further details, please review your Advance notice found on your portal.'
											: 'Please be aware by choosing this option, interest will be payable. For further details, please check your Granite Finance documentation.'}
									</Typography>
								)}
							</RadioCard>

							<RadioCard value='single' title='Single Card Payment' selected={selectedMethod === 'single'}>
								<Typography sx={{ fontSize: 14, mt: 2, lineHeight: 1 }}>Payment Amount</Typography>
								<Typography
									sx={{
										lineHeight: 1,
										fontWeight: 'bold',
										fontSize: 35,
										color: 'primary.main',
									}}
								>
									{formatPounds(payInFullOption.totalAmountPayable ?? 0)}
								</Typography>
								<PriceBreakdown
									price={payInFullOption.totalAmountPayable}
									fee={midTermAdjustmentResponse.coverDetails.fee}
									product={policy.product}
								/>
								<Typography marginTop={2} fontSize={14}>
									Paying for this change as a single payment won&apos;t affect your current monthly payment plan.
								</Typography>
							</RadioCard>
						</Box>
					</RadioGroup>
				</Grid>
			</Grid>
			<Collapse in={selectedMethod === 'single'}>
				<Box
					maxWidth='md'
					margin='auto'
					sx={(theme) => ({
						paddingTop: 3,
						paddingBottom: 2,
						marginTop: 2,
						marginBottom: 2,
						borderRadius: 2,
						border: `1px solid ${theme.palette.divider}`,
						backgroundColor: `${theme.palette.background.paper}`,
						boxShadow: `0 4px 12px ${theme.palette.primary.main}10`,
						padding: 3,
						position: 'relative',
						'&::before': {
							content: '""',
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '4px',
							background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}80)`,
							borderTopLeftRadius: 8,
							borderTopRightRadius: 8,
						},
					})}
				>
					<Typography
						variant='subtitle1'
						fontWeight='500'
						sx={(theme) => ({
							mb: 2,
							display: 'flex',
							alignItems: 'center',
							'&::before': {
								content: '""',
								display: 'inline-block',
								width: '8px',
								height: '8px',
								borderRadius: '50%',
								backgroundColor: theme.palette.primary.main,
								marginRight: '10px',
							},
						})}
					>
						{midTermAdjustmentResponse.existingCardPaymentMethods.length
							? 'Please select an existing card for payment, or use a new card.'
							: 'Please use a new card for payment.'}
					</Typography>
					{midTermAdjustmentResponse.paymentProvider === PaymentProvider2.STRIPE ? (
						<StripePaymentWrapper payInFullOption={payInFullOption} toggleModal={toggleModal} />
					) : (
						<BraintreeCardPaymentSection payInFullOption={payInFullOption} toggleModal={toggleModal} />
					)}
				</Box>
			</Collapse>
			<Collapse in={selectedMethod === 'instalment'}>
				<Box
					maxWidth='md'
					margin='auto'
					sx={(theme) => ({
						paddingTop: 3,
						paddingBottom: 3,
						marginTop: 2,
						marginBottom: 2,
						borderRadius: 2,
						border: `1px solid ${theme.palette.divider}`,
						backgroundColor: `${theme.palette.background.paper}`,
						boxShadow: `0 4px 12px ${theme.palette.primary.main}10`,
						padding: 3,
						position: 'relative',
						'&::before': {
							content: '""',
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '4px',
							background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
							borderTopLeftRadius: 8,
							borderTopRightRadius: 8,
						},
					})}
				>
					<Box sx={{ px: { xs: 1, md: 2 } }}>
						<Typography sx={{ pb: 2 }}>
							No payment will be taken now. The total cost will be spread across your remaining instalments. If your
							Direct Debit payment is scheduled with the next 14 days your payments will not be updated until your
							following payment is due. Please refer to your Direct Debit Schedule located within your policy documents
							once you have processed this change.
						</Typography>
						{financeOption.unequalInstalmentAmount && financeOption.unequalInstalmentAmount > 0 ? (
							<>
								<DisplayField label='The next instalment will cost'>
									{formatPounds(financeOption.unequalInstalmentAmount)}
								</DisplayField>
								{financeOption.appliedInstalmentCount && financeOption.appliedInstalmentCount > 1 && (
									<DisplayField
										label={`The ${
											(financeOption.appliedInstalmentCount ?? 0) - 1
										} remaining instalments will each cost`}
									>
										{formatPounds(financeOption.standardInstalmentAmount ?? 0)}
									</DisplayField>
								)}
							</>
						) : (
							<>
								{financeOption.appliedInstalmentCount && financeOption.appliedInstalmentCount > 1 && (
									<DisplayField
										label={`The ${financeOption.appliedInstalmentCount ?? 0} remaining instalments will each cost`}
									>
										{formatPounds(financeOption.standardInstalmentAmount ?? 0)}
									</DisplayField>
								)}
							</>
						)}
					</Box>
					<Typography
						sx={{
							mt: 2,
							px: { xs: 1, md: 2 },
							fontStyle: 'italic',
							borderLeft: '3px solid',
							borderColor: 'primary.main',
							pl: 2,
							py: 1,
							bgcolor: 'primary.light + "08"',
						}}
					>
						By selecting the option to add this to your{' '}
						{isProtectAdultDriver ? `finance agreement` : `Direct Debit payments`}, you are confirming that your new
						monthly payments are affordable now and for the duration of the agreement and that you have budgeted to
						ensure that you will be able to make those monthly repayments.
					</Typography>
				</Box>
				<ActionButtons>
					<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
					<PaymentButton onClick={submitPaymentForm} icon='Bank'>
						Process Payment
					</PaymentButton>
				</ActionButtons>
			</Collapse>
		</Box>
	);
};

export default CardAndFinancePaymentOptions;
